import { useState, useEffect, useMemo, useRef } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import {
  TrendingUp, Filter, Loader2, FileDown, Plus, X, FileSpreadsheet,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "../../lib/api";
import DatePickerInput from "../DatePickerInput";
import SearchableCombo from "./SearchableCombo";

/**
 * SiteTrendHistory — Iter 380 / Iter 383
 *
 * Site-level Trend & History with:
 *   • Filter: All equipment | one
 *   • Filter: multi-parameter (pill chips)
 *   • Multi-date-range comparison (up to 3 ranges — overlay lines to
 *     compare "how did this param perform in Feb 2026 vs Dec 2025?")
 *   • Chart PDF export (uses the same HidroVerse branded shell as the
 *     Project Planner / register report)
 *
 * X-axis normalises to "Day N from range start" so ranges of different
 * absolute dates can overlay. Tooltip still shows the true date per point.
 */
const LINE_COLORS = ["#1fb6c1", "#f59e0b", "#a78bfa", "#10b981", "#ef4444", "#6366f1", "#ec4899", "#eab308"];
const DASH_STYLES = ["", "6 3", "2 3", "4 2 1 2"];
const MAX_RANGES = 3;

function daysAgo(n) {
  return new Date(Date.now() - n * 86400e3).toISOString().slice(0, 10);
}
function today() {
  return new Date().toISOString().slice(0, 10);
}

export default function SiteTrendHistory({ site, equipmentBySite }) {
  const equipment = equipmentBySite?.[site?.id] || [];
  const [eqFilter, setEqFilter] = useState("");
  const [paramOptions, setParamOptions] = useState([]);
  const [selectedParams, setSelectedParams] = useState([]);
  const [ranges, setRanges] = useState([
    { id: "r1", from: daysAgo(30), to: today(), label: "Last 30 days" },
  ]);
  const [rowsByRange, setRowsByRange] = useState({});
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [exporting, setExporting] = useState(false);
  const chartRef = useRef(null);

  useEffect(() => {
    (async () => {
      const eqs = eqFilter ? equipment.filter((e) => e.id === eqFilter) : equipment;
      const all = await Promise.all(eqs.map(async (eq) => {
        try {
          const { data } = await api.get(`/perf/parameters?equipment_id=${eq.id}`);
          return data.map((p) => ({ ...p, equipment_id: eq.id, equipment_name: eq.name }));
        } catch { return []; }
      }));
      setParamOptions(all.flat());
    })();
  }, [site?.id, eqFilter, equipment.length]);

  useEffect(() => {
    if (!site?.id) return;
    setLoading(true);
    setErr("");
    Promise.all(ranges.map(async (r) => {
      let url = `/perf/history?site_id=${site.id}&date_from=${r.from}&date_to=${r.to}`;
      if (eqFilter) url += `&equipment_id=${eqFilter}`;
      try {
        const { data } = await api.get(url);
        return [r.id, data.rows || []];
      } catch { return [r.id, []]; }
    }))
      .then((pairs) => setRowsByRange(Object.fromEntries(pairs)))
      .catch((e) => setErr(formatErr(e)))
      .finally(() => setLoading(false));
  }, [site?.id, eqFilter, ranges]);

  const toggleParam = (pid) => {
    setSelectedParams((s) => s.includes(pid) ? s.filter((x) => x !== pid) : [...s, pid]);
  };

  const addRange = () => {
    if (ranges.length >= MAX_RANGES) {
      toast.error(`Max ${MAX_RANGES} ranges — remove one first.`);
      return;
    }
    const nextIdx = ranges.length + 1;
    // default to the previous month
    const baseDays = 30;
    const from = daysAgo(baseDays * (nextIdx + 1));
    const to = daysAgo(baseDays * nextIdx);
    setRanges((rs) => [...rs, { id: `r${Date.now()}`, from, to, label: `Range ${nextIdx}` }]);
  };
  const removeRange = (id) => setRanges((rs) => rs.filter((r) => r.id !== id));
  const updateRange = (id, patch) => setRanges((rs) => rs.map((r) => {
    if (r.id !== id) return r;
    // Iter 388 — if the user manually edits from/to, drop the preset
    // label ("Last 30 days") so both the on-screen chart and PDF
    // reflect the actual date range they selected.
    const touched = ("from" in patch) || ("to" in patch);
    return { ...r, ...patch, ...(touched ? { label: "" } : {}) };
  }));

  /**
   * chartData shape: [{ day: 0, "r1__pid1": val, "r2__pid1": val, ... }, ...]
   * Key format: `${rangeId}__${paramId}`. Day N is the offset from
   * the range's `from` date so multiple ranges align.
   */
  const chartData = useMemo(() => {
    const byDay = {};
    ranges.forEach((r) => {
      const from = new Date(r.from + "T00:00:00");
      const rows = (rowsByRange[r.id] || [])
        .filter((row) => !row.superseded_by && selectedParams.includes(row.parameter_id));
      rows.forEach((row) => {
        const d = new Date((row.reading_date || "").slice(0, 10) + "T00:00:00");
        const offset = Math.round((d - from) / 86400e3);
        if (!byDay[offset]) byDay[offset] = { day: offset };
        byDay[offset][`${r.id}__${row.parameter_id}`] = row.value;
        // stash the true date for tooltip
        byDay[offset][`${r.id}__${row.parameter_id}__date`] = (row.reading_date || "").slice(0, 10);
      });
    });
    return Object.values(byDay).sort((a, b) => a.day - b.day);
  }, [rowsByRange, ranges, selectedParams]);

  const paramNameById = useMemo(() => {
    const m = {};
    paramOptions.forEach((p) => {
      m[p.id] = `${p.name}${p.unit ? ` (${p.unit})` : ""}${eqFilter ? "" : ` · ${p.equipment_name}`}`;
    });
    return m;
  }, [paramOptions, eqFilter]);

  const rangeLabel = (r, idx) => r.label || `${r.from} → ${r.to}`;

  const exportChartPdf = async () => {
    if (!selectedParams.length) {
      toast.error("Pick at least one parameter to plot.");
      return;
    }
    setExporting(true);
    try {
      // Iter 387 — Server-side render via matplotlib using the SAME
      // ranges + parameter_ids the on-screen chart is drawing. No
      // more client SVG-to-PNG rasterisation flakiness.
      const legend = ranges.map((r, idx) => ({
        id: r.id,
        label: rangeLabel(r, idx),
        from: r.from,
        to: r.to,
      }));
      const paramLegend = selectedParams.map((pid) => ({
        id: pid,
        label: paramNameById[pid] || pid.slice(0, 6),
      }));
      const { data } = await api.post("/perf/report/chart-pdf", {
        site_id: site?.id,
        site_name: site?.name || "",
        equipment_id: eqFilter || null,
        equipment_name: equipment.find((e) => e.id === eqFilter)?.name || null,
        parameter_ids: selectedParams,
        ranges: legend,
        parameters: paramLegend,
      }, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `perf-trend-${(site?.name || "site").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      toast.error(`Chart export failed: ${formatErr(e)}`);
    } finally {
      setExporting(false);
    }
  };

  // 2026-02-28 (iter 419.n, Dirk) — Excel export of the analytics view.
  // Uses the /perf/report/xlsx endpoint (same as the register) so the
  // sheet has one row per parameter and one column per day. When the
  // user has multiple date ranges on screen we export the union window
  // (min "from" → max "to") so nothing they can see on-chart is missing
  // from the sheet.
  const exportXlsx = async () => {
    setExporting(true);
    try {
      const froms = ranges.map((r) => r.from).filter(Boolean).sort();
      const tos = ranges.map((r) => r.to).filter(Boolean).sort();
      const dateFrom = froms[0];
      const dateTo = tos[tos.length - 1];
      const qs = new URLSearchParams();
      if (eqFilter) qs.set("equipment_id", eqFilter);
      else if (site?.id) qs.set("site_id", site.id);
      if (dateFrom) qs.set("date_from", dateFrom);
      if (dateTo) qs.set("date_to", dateTo);
      if (dateFrom && dateTo) {
        const spanDays = Math.max(
          1,
          Math.round((new Date(dateTo) - new Date(dateFrom)) / 86400e3) + 1,
        );
        qs.set("days", String(spanDays));
      }
      const { data } = await api.get(`/perf/report/xlsx?${qs.toString()}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `perf-analytics-${(site?.name || "site").replace(/\s+/g, "_")}-${dateFrom || "start"}_to_${dateTo || "end"}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      toast.error(`Excel export failed: ${formatErr(e)}`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="hv-card overflow-hidden mb-3" data-testid="site-trend-history">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-white/5 flex-wrap">
        <TrendingUp className="w-4 h-4 text-[#1fb6c1]" />
        <span className="text-sm font-medium">Site trend & history</span>
        {loading && <Loader2 className="w-3 h-3 animate-spin text-[#647087]" />}
        <span className="text-[10px] text-[#647087]">Overlay parameters across up to {MAX_RANGES} date ranges</span>
        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            onClick={exportXlsx}
            disabled={exporting}
            className="hv-btn hv-btn-ghost text-[10px] py-1 disabled:opacity-40"
            data-testid="site-trend-export-xlsx"
            title="Export analytics data as Excel spreadsheet"
          >
            <FileSpreadsheet className="w-3 h-3" /> {exporting ? "…" : "Excel"}
          </button>
          <button
            type="button"
            onClick={exportChartPdf}
            disabled={exporting || !selectedParams.length}
            className="hv-btn hv-btn-primary text-[10px] py-1 disabled:opacity-40"
            data-testid="site-trend-export-chart-pdf"
            title="Export current chart as HidroVerse-branded PDF"
          >
            <FileDown className="w-3 h-3" /> {exporting ? "…" : "Chart PDF"}
          </button>
        </div>
      </div>

      <div className="px-3 py-2 border-b border-white/5 flex items-start gap-3 flex-wrap text-xs" data-testid="site-trend-filters">
        <div className="flex items-center gap-1.5">
          <Filter className="w-3 h-3 text-[#647087]" />
          <div className="w-[200px]">
            <SearchableCombo
              value={eqFilter}
              onChange={(v) => { setEqFilter(v); setSelectedParams([]); }}
              options={equipment.map((eq) => ({ id: eq.id, label: eq.name }))}
              allLabel="All equipment"
              placeholder="Search equipment…"
              testId="site-trend-eq"
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <div className="text-[10px] uppercase tracking-wider text-[#647087]">Date ranges</div>
          {ranges.map((r, idx) => (
            <div key={r.id} className="flex items-center gap-1.5" data-testid={`site-trend-range-${idx}`}>
              <span
                className="inline-block w-3 h-3 rounded-full border border-white/20 shrink-0"
                style={{ background: LINE_COLORS[idx % LINE_COLORS.length] }}
                title={rangeLabel(r, idx)}
              />
              <div className="w-[130px]">
                <DatePickerInput
                  value={r.from}
                  onChange={(iso) => updateRange(r.id, { from: iso })}
                  testId={`site-trend-range-${idx}-from`}
                />
              </div>
              <span className="text-[#647087]">→</span>
              <div className="w-[130px]">
                <DatePickerInput
                  value={r.to}
                  onChange={(iso) => updateRange(r.id, { to: iso })}
                  testId={`site-trend-range-${idx}-to`}
                />
              </div>
              {ranges.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeRange(r.id)}
                  className="text-[#647087] hover:text-rose-300 p-1"
                  title="Remove this range"
                  data-testid={`site-trend-range-${idx}-remove`}
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
          {ranges.length < MAX_RANGES && (
            <button
              type="button"
              onClick={addRange}
              className="hv-btn hv-btn-ghost text-[10px] py-0.5"
              data-testid="site-trend-add-range"
            >
              <Plus className="w-3 h-3" /> Add date range
            </button>
          )}
        </div>
      </div>

      <div className="px-3 py-2 border-b border-white/5">
        <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1">Parameters</div>
        <div className="flex items-center gap-1 flex-wrap">
          {paramOptions.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => toggleParam(p.id)}
              className={`text-[10px] px-2 py-0.5 rounded-full border transition ${selectedParams.includes(p.id) ? "border-[#1fb6c1] bg-[#1fb6c1]/15 text-[#1fb6c1]" : "border-white/10 text-[#a0aabf] hover:border-white/20"}`}
              data-testid={`site-trend-param-${p.id}`}
            >
              {p.name}{p.unit ? ` (${p.unit})` : ""}
            </button>
          ))}
          {selectedParams.length > 0 && (
            <button
              type="button"
              onClick={() => setSelectedParams([])}
              className="text-[10px] text-rose-300 ml-1"
              data-testid="site-trend-clear-params"
            >Clear</button>
          )}
        </div>
      </div>

      {err && <div className="p-3 text-sm text-rose-300">{err}</div>}

      {selectedParams.length === 0 ? (
        <div className="p-6 text-center text-xs text-[#647087]">Pick one or more parameters to draw the trend.</div>
      ) : chartData.length === 0 ? (
        <div className="p-6 text-center text-xs text-[#647087]">No readings in the selected windows.</div>
      ) : (
        <div ref={chartRef} className="p-2" style={{ width: "100%", height: 360 }}>
          <ResponsiveContainer>
            <LineChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 24 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis
                dataKey="day"
                type="number"
                domain={["dataMin", "dataMax"]}
                tick={{ fill: "#647087", fontSize: 10 }}
                tickMargin={4}
                label={{ value: "Day offset from range start", position: "insideBottom", offset: -8, fill: "#647087", fontSize: 10 }}
              />
              <YAxis tick={{ fill: "#647087", fontSize: 10 }} width={40} />
              <Tooltip
                contentStyle={{ background: "#0a0b0e", border: "1px solid rgba(255,255,255,0.1)", fontSize: 12, borderRadius: 6 }}
                labelStyle={{ color: "#a0aabf" }}
                labelFormatter={(d) => `Day ${d}`}
              />
              <Legend wrapperStyle={{ fontSize: 10, color: "#a0aabf" }} />
              {ranges.flatMap((r, rIdx) =>
                selectedParams.map((pid, pIdx) => (
                  <Line
                    key={`${r.id}__${pid}`}
                    type="monotone"
                    dataKey={`${r.id}__${pid}`}
                    name={`${paramNameById[pid] || pid.slice(0, 6)} · ${rangeLabel(r, rIdx)}`}
                    stroke={LINE_COLORS[(pIdx * 2 + rIdx) % LINE_COLORS.length]}
                    strokeWidth={2}
                    strokeDasharray={DASH_STYLES[rIdx % DASH_STYLES.length]}
                    dot={{ r: 2.5 }}
                    activeDot={{ r: 5 }}
                    connectNulls
                    isAnimationActive={false}
                  />
                )),
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
